// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Grid, IconButton, Typography } from '@mui/material'
import { FC } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import { Alert } from '../../common/Alert'
import { CustomController } from '../../common/CustomController'
// others
import { useUpdateUserGeneralInfoMutation, UserStatus } from '../../../../generated'
import { USER_UPDATE_SUCCESS } from '../../../constants'
import { DispatchAndState, UpdateInitialsInputProps } from '../../../interfaceTypes'
import { updateInitialsSchema } from '../../../validationSchema'
import { UserAction, UserState } from '../userReducer'

export const UpdateInitials: FC<DispatchAndState<UserState, UserAction>> = ({ state, refetch }) => {
  const { currentUser } = state || {}
  const { id, initials, status } = currentUser || {}

  const updateInitialsMethods = useForm<UpdateInitialsInputProps>({
    mode: 'all',
    resolver: yupResolver(updateInitialsSchema),
    defaultValues: {
      id,
      initials: initials || ''
    }
  })

  const { handleSubmit: updateInitialsHandleSubmit, setValue } = updateInitialsMethods

  const [updateUserGeneralInfoMutation, { loading }] = useUpdateUserGeneralInfoMutation({
    onError(error) {
      const { graphQLErrors } = error
      if (graphQLErrors) {
        graphQLErrors.map(({ message }) => {
          Alert.error(message)
        })
      }
    },
    onCompleted(data) {
      if (data) {
        const { updateUserGeneralInfo } = data
        const { response, user } = updateUserGeneralInfo || {}
        if (response) {
          const { status: responseStatus } = response
          if (responseStatus === 200) {
            Alert.success(USER_UPDATE_SUCCESS)
            if (user) {
              setValue('initials', user.initials || '')
            }
            refetch?.()
          }
        }
      }
    }
  })

  const initialsSubmitHandler: SubmitHandler<UpdateInitialsInputProps> = async (data: UpdateInitialsInputProps) => {
    await updateUserGeneralInfoMutation({
      variables: {
        user: {
          id: id || '',
          initials: data.initials || undefined
        }
      }
    })
  }

  return (
    <FormProvider {...updateInitialsMethods}>
      <form onSubmit={updateInitialsHandleSubmit(initialsSubmitHandler)}>
        <Box pb={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box flex={1}>
              <Typography variant="h5" fontSize="14px">
                Initials:
              </Typography>
            </Box>

            <IconButton type="submit" aria-label="Save" disabled={status !== UserStatus.Active || loading}>
              <SaveRoundedIcon color={status === UserStatus.Active ? 'primary' : 'disabled'} />
            </IconButton>
          </Box>

          <Grid container spacing={2} rowSpacing={0.5}>
            <Grid item md={6} sm={12}>
              <CustomController controllerName="initials" controllerLabel="Initials" fieldType="text" />
            </Grid>
          </Grid>
        </Box>
      </form>
    </FormProvider>
  )
}
